import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Platform-admin auth state. Parallel to (and intentionally disjoint
 * from) `useAuthStore` — the persisted-storage key is different, so a
 * user signed into the tenant app and a platform admin signed into the
 * platform shell coexist in the same browser without colliding.
 */

interface PlatformAdmin {
  id: string;
  email: string;
  fullName: string;
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
}

interface PlatformAuthState {
  accessToken: string | null;
  expiresAt: number | null;
  admin: PlatformAdmin | null;
  setSession(p: { accessToken: string; expiresIn: number; admin: PlatformAdmin }): void;
  setToken(token: string, expiresIn: number): void;
  clear(): void;
}

export const usePlatformAuthStore = create<PlatformAuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      expiresAt: null,
      admin: null,
      setSession: ({ accessToken, expiresIn, admin }) =>
        set({
          accessToken,
          expiresAt: Date.now() + expiresIn * 1000,
          admin,
        }),
      setToken: (accessToken, expiresIn) =>
        set({ accessToken, expiresAt: Date.now() + expiresIn * 1000 }),
      clear: () => set({ accessToken: null, expiresAt: null, admin: null }),
    }),
    {
      // Same storage strategy as the tenant store; different key so the
      // two sessions don't trample each other.
      name: 'erp-platform-auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export type { PlatformAdmin };
